import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ThemedText } from '@/components/themed-text';
import api from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { showToast } from '@/store/toastStore';
import { isInTrialPeriod, trialDaysRemaining } from '@/utils/trial';
import { useQuery } from '@tanstack/react-query';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  useWindowDimensions,
  View,
} from 'react-native';
import { FadeInDown } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { AnimatedView } from '@/components/ui/animated';
import { captureReturnToAppFromUrl } from '@/lib/auth/web-onboarding-return-to-app';
import { useAppleIAP } from '@/hooks/useAppleIAP';
import { getAppleProductId } from '@/services/appleIapService';
import { getGoogleProductId } from '@/services/googlePlayIapService';
import { fetchStorePlanPrices } from '@/services/storePrices';
import { planTierOf } from '@/utils/plan-access';
import { TeamPlansPanel } from '@/components/subscription/team-plans-panel';
import { BecomeTeamSheet } from '@/components/team/become-team-sheet';
import { useMembership } from '@/hooks/use-membership';
import { useTeamPlanAvailable } from '@/hooks/use-team-pricing';
import { INTRO_OFFER_PRICES, fetchIntroOfferEligible } from '@/services/introOffer';

const SUPPORT_WHATSAPP = '5511999999999'; // Configurar com número real

const PRIMARY = '#FD5001';

const PLAN_CONFIG: Record<
  string,
  { icon: keyof typeof Ionicons.glyphMap; tagline?: string; cta: string }
> = {
  basic: {
    icon: 'rocket-outline',
    tagline: 'Para começar',
    cta: 'Escolher plano',
  },
  professional: {
    icon: 'star-outline',
    tagline: 'Recomendado',
    cta: 'Escolher plano',
  },
  enterprise: {
    icon: 'business-outline',
    tagline: 'Escala',
    cta: 'Escolher plano',
  },
  custom: {
    icon: 'chatbubbles-outline',
    tagline: 'Sob medida',
    cta: 'Contatar suporte',
  },
};

const CAROUSEL_PADDING = 16;
const CAROUSEL_GAP = 12;
const CAROUSEL_PEEK = 36;

type Plan = {
  id: string;
  name: string;
  description: string;
  priceMonthly: number;
  priceId: string | null;
  features: string[];
  highlight?: boolean;
  capacity?: string;
  targetAudience?: string;
};

type PlanCardProps = {
  plan: Plan;
  index: number;
  /** Largura fixa no carrossel; no grid web o card ocupa a coluna inteira. */
  cardWidth?: number;
  marginRight?: number;
  showTrialMode: boolean;
  selectingPlanId: string | null;
  onSelectPlan: (planId: string, priceId: string | null, amountMonthly?: number) => void;
  /** Preço do 1º mês quando a conta ainda tem a oferta de boas-vindas deste plano. */
  introPrice?: number | null;
};

const formatPrice = (value: number) =>
  value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function PlanCard({
  plan,
  index,
  cardWidth,
  marginRight,
  showTrialMode,
  selectingPlanId,
  onSelectPlan,
  introPrice,
}: PlanCardProps) {
  const config = PLAN_CONFIG[plan.id] ?? {
    icon: 'ellipse-outline' as const,
    cta: plan.id === 'custom' ? 'Contatar suporte' : 'Assinar',
  };
  const isHighlight = plan.highlight;
  const isCustom = plan.id === 'custom';
  const showTrialBadge = showTrialMode && !isCustom;

  return (
    <AnimatedView
      entering={FadeInDown.duration(500).delay(250 + index * 80)}
      // O container (linha do carrossel / grid) estica: altura vem do card mais alto.
      style={{
        ...(cardWidth != null ? { width: cardWidth } : null),
        marginRight: marginRight ?? 0,
      }}
    >
      {/* flex-1 encadeado: o card preenche a altura esticada pelo container */}
      <Pressable
        onPress={() => onSelectPlan(plan.id, plan.priceId, plan.priceMonthly)}
        className="flex-1"
      >
        <Card
          className={`flex-1 ${
            isHighlight
              ? 'border-2 border-primary'
              : isCustom
                ? 'border border-dashed border-primary-200'
                : 'border border-neutral-900/5'
          }`}
        >
          <View className="flex-1 p-5">
            {(config.tagline || showTrialBadge) && (
              <View className="mb-4 flex-row flex-wrap items-center gap-2">
                {config.tagline && (
                  <View
                    className={`flex-row items-center gap-1.5 rounded-full px-3 py-1 ${
                      isHighlight ? 'bg-primary' : 'bg-primary-50'
                    }`}
                  >
                    <Ionicons name={config.icon} size={14} color={isHighlight ? '#fff' : PRIMARY} />
                    <ThemedText
                      className={`font-semibold text-xs ${
                        isHighlight ? 'text-white' : 'text-primary'
                      }`}
                    >
                      {config.tagline}
                    </ThemedText>
                  </View>
                )}
                {showTrialBadge && (
                  <View className="flex-row items-center gap-1.5 rounded-full bg-success-50 px-3 py-1">
                    <Ionicons className="text-success-600" name="sparkles-outline" size={14} />
                    <ThemedText className="font-semibold text-xs text-success-600">
                      Teste grátis
                    </ThemedText>
                  </View>
                )}
              </View>
            )}

            <View
              className={`mb-3 h-14 w-14 items-center justify-center rounded-2xl ${
                isHighlight ? 'bg-primary' : 'bg-primary-50'
              }`}
            >
              <Ionicons
                name={config.icon}
                size={28}
                className={isHighlight ? 'text-white' : 'text-primary dark:text-white'}
              />
            </View>

            <ThemedText className="font-bold text-xl text-neutral-900">{plan.name}</ThemedText>

            {plan.capacity && (
              <ThemedText className="mt-1 font-semibold text-sm text-primary">
                {plan.capacity}
              </ThemedText>
            )}

            {plan.targetAudience && (
              <View className="mt-2 rounded-xl bg-primary-50 px-3 py-2">
                <ThemedText className="text-sm leading-snug text-neutral-700">
                  {plan.targetAudience}
                </ThemedText>
              </View>
            )}

            <ThemedText className="mt-2 text-sm leading-snug text-neutral-500">
              {plan.description}
            </ThemedText>

            {introPrice != null && plan.priceMonthly > 0 ? (
              <View className="mt-4">
                <View className="flex-row items-baseline gap-1">
                  <ThemedText
                    className="font-extrabold text-3xl text-primary"
                    style={{ fontVariant: ['tabular-nums'] }}
                  >
                    R$ {formatPrice(introPrice)}
                  </ThemedText>
                  <ThemedText className="text-base text-neutral-400">no 1º mês</ThemedText>
                </View>
                <ThemedText className="mt-0.5 text-sm text-neutral-500">
                  Depois R$ {formatPrice(plan.priceMonthly)}/mês
                </ThemedText>
              </View>
            ) : plan.priceMonthly > 0 ? (
              <View className="mt-4 flex-row items-baseline gap-1">
                <ThemedText
                  className="font-extrabold text-3xl text-neutral-900"
                  style={{ fontVariant: ['tabular-nums'] }}
                >
                  R${' '}
                  {plan.priceMonthly.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </ThemedText>
                <ThemedText className="text-base text-neutral-400">/mês</ThemedText>
              </View>
            ) : (
              <View className="mt-4">
                <ThemedText className="font-bold text-lg text-neutral-900">Sob consulta</ThemedText>
              </View>
            )}

            {/* flex-1 empurra o botão pro rodapé: CTAs alinhados entre os cards */}
            <View className="mt-5 flex-1 gap-2">
              {plan.features.map((f, i) => (
                <View key={i} className="flex-row items-start gap-2">
                  <View className="mt-0.5 h-5 w-5 items-center justify-center rounded-full bg-primary-100 dark:bg-primary">
                    <Ionicons className="text-primary dark:text-white" name="checkmark" size={12} />
                  </View>
                  <ThemedText className="flex-1 text-sm leading-snug text-neutral-700">
                    {f}
                  </ThemedText>
                </View>
              ))}
            </View>

            <Button
              onPress={() => onSelectPlan(plan.id, plan.priceId, plan.priceMonthly)}
              variant={isCustom ? 'outline' : 'default'}
              className={`mt-6 w-full ${isCustom ? 'border-primary bg-card' : 'bg-primary'}`}
            >
              <ThemedText
                className={`font-semibold text-sm ${isCustom ? 'text-primary' : 'text-white'}`}
                numberOfLines={1}
              >
                {selectingPlanId === plan.id ? 'Aguarde...' : config.cta}
              </ThemedText>
              <Ionicons name="arrow-forward" size={18} color={isCustom ? PRIMARY : '#fff'} />
            </Button>
          </View>
        </Card>
      </Pressable>
    </AnimatedView>
  );
}

export default function PlansScreen() {
  const { width } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const isNative = Platform.OS !== 'web';
  const isStoreBilling = Platform.OS === 'ios' || Platform.OS === 'android';
  const useCarousel = isNative || width < 768;
  const carouselCardWidth = Math.min(width - CAROUSEL_PADDING * 2 - CAROUSEL_PEEK, 340);
  const carouselSnapInterval = carouselCardWidth + CAROUSEL_GAP;

  const { user, token, fetchProfile } = useAuthStore();
  const [selectingPlanId, setSelectingPlanId] = useState<string | null>(null);
  const { membership, isSolo, isOwner, can } = useMembership();
  const teamPlanAvailable = useTeamPlanAvailable({ enabled: !!membership });
  // Organização vê a calculadora; solo, os planos de sempre.
  const isTeamBilling = membership?.type === 'TEAM' && can('billing');
  const [becomeTeamOpen, setBecomeTeamOpen] = useState(false);

  const appleIAP = useAppleIAP({
    onSuccess: () => {
      router.replace('/subscription/success');
    },
    onError: (message) => {
      showToast(message, 'error');
      setSelectingPlanId(null);
    },
  });

  useEffect(() => {
    if (token && !user) {
      fetchProfile();
    }
  }, [token, user, fetchProfile]);

  const isTrialActive = useMemo(
    () => isInTrialPeriod(user?.providerProfile?.trialEndsAt ?? null),
    [user?.providerProfile?.trialEndsAt],
  );
  const trialDaysLeft = useMemo(
    () => trialDaysRemaining(user?.providerProfile?.trialEndsAt ?? null),
    [user?.providerProfile?.trialEndsAt],
  );
  const hasActiveSubscription = useMemo(() => {
    const subId = user?.providerProfile?.subscriptionId;
    const subStatus = user?.providerProfile?.subscriptionStatus;
    return !!subId && subStatus !== 'canceled';
  }, [user?.providerProfile?.subscriptionId, user?.providerProfile?.subscriptionStatus]);
  // Teste grátis só a plataforma ativa (painel admin): aqui ele só é mostrado.
  const isFreePlan = planTierOf(user) === 'FREE';

  useEffect(() => {
    if (Platform.OS === 'web') {
      captureReturnToAppFromUrl();
    }
  }, []);

  const { data: apiPlans, isLoading } = useQuery({
    queryKey: ['stripe-plans'],
    queryFn: async () => {
      const res = await api.get('/stripe/plans');
      const all = res.data as Plan[];
      // No app só entra o que se compra pela loja (Apple 3.1.1, Google Play
      // Billing); o sob medida fecha por WhatsApp e fica só na web.
      return isStoreBilling ? all.filter((p) => p.id !== 'custom') : all;
    },
  });

  // No app, o preço do cartão é o da loja (App Store/Google Play têm tabela
  // própria); sem resposta da loja, fica o da API.
  const { data: storePrices } = useQuery({
    queryKey: ['store-plan-prices', Platform.OS, apiPlans?.map((p) => p.id).join()],
    queryFn: () => fetchStorePlanPrices(apiPlans!.map((p) => p.id)),
    enabled: isStoreBilling && !!apiPlans,
    staleTime: 60 * 60 * 1000,
  });
  const plans = useMemo(
    () =>
      apiPlans?.map((plan) =>
        storePrices?.[plan.id] != null ? { ...plan, priceMonthly: storePrices[plan.id] } : plan,
      ),
    [apiPlans, storePrices],
  );

  const handleSelectPlan = async (
    planId: string,
    priceId: string | null,
    amountMonthly?: number,
  ) => {
    if (planId === 'custom') {
      const msg = 'Olá! Gostaria de saber mais sobre o plano personalizado da Controlapag.';
      const url = `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(msg)}`;
      if (Platform.OS === 'web') {
        window.open(url, '_blank');
      } else {
        Linking.openURL(url);
      }
      return;
    }

    // App: compra pela loja do aparelho (App Store 3.1.1 e Google Play Billing).
    if (isStoreBilling) {
      const productId =
        Platform.OS === 'ios' ? getAppleProductId(planId) : getGoogleProductId(planId);
      if (!productId) {
        showToast('Produto não disponível. Tente novamente em breve.', 'error');
        return;
      }
      setSelectingPlanId(planId);
      await appleIAP.purchaseSubscription(planId);
      // Result is delivered asynchronously via onSuccess / onError callbacks
      return;
    }

    // Web: checkout do Stripe
    if (!priceId) return;
    router.push({
      pathname: '/subscription/checkout',
      params: {
        planId,
        priceId,
        ...(amountMonthly != null ? { amount: String(amountMonthly) } : {}),
      },
    });
  };

  const isApplePurchasing =
    isStoreBilling && (appleIAP.state === 'purchasing' || appleIAP.state === 'validating');

  // Oferta de boas-vindas: a loja (ou a API, no site) diz se a conta ainda
  // pega; a compra do plano já aplica sozinha, o card só avisa.
  const { data: introEligible } = useQuery({
    queryKey: ['intro-offer-eligible'],
    queryFn: () => fetchIntroOfferEligible().catch(() => false),
    enabled: !!user && !hasActiveSubscription,
    staleTime: Infinity,
  });
  const introPriceOf = (planId: string) => (introEligible && INTRO_OFFER_PRICES[planId]) || null;

  const planCardProps = {
    showTrialMode: isTrialActive && !hasActiveSubscription,
    selectingPlanId,
    onSelectPlan: handleSelectPlan,
  };

  if (isLoading || !plans) {
    return (
      <View
        className="flex-1 items-center justify-center bg-background"
        style={{ paddingTop: insets.top }}
      >
        <ActivityIndicator size="large" color={PRIMARY} />
      </View>
    );
  }

  const showTrialMode = isTrialActive && !hasActiveSubscription;

  return (
    <View className="flex-1 bg-background">
      <ScrollView
        keyboardShouldPersistTaps="handled"
        className="flex-1"
        contentContainerStyle={{
          paddingTop: insets.top,
          paddingBottom: Math.max(insets.bottom, 48),
        }}
        showsVerticalScrollIndicator={false}
        nestedScrollEnabled
      >
        {showTrialMode && (
          <AnimatedView
            entering={FadeInDown.duration(500).delay(60)}
            className="mx-4 mt-4 overflow-hidden rounded-2xl bg-primary p-4"
          >
            <View className="flex-row items-start justify-between gap-3">
              <View className="flex-1">
                <View className="flex-row items-center gap-2">
                  <View className="h-8 w-8 items-center justify-center rounded-full bg-white/20">
                    <Ionicons name="gift-outline" size={18} color="#fff" />
                  </View>
                  <ThemedText className="font-extrabold text-base text-white">
                    Teste grátis
                  </ThemedText>
                </View>
                <ThemedText className="mt-2 text-sm leading-relaxed text-white/90">
                  Restam{' '}
                  <ThemedText className="font-extrabold text-white">{trialDaysLeft}</ThemedText>{' '}
                  dia(s) com tudo liberado. Assine antes de acabar para não perder nada — depois
                  dele, sua conta segue no plano gratuito.
                </ThemedText>
              </View>
              <View className="rounded-full bg-white/20 px-3 py-1">
                <ThemedText className="font-bold text-xs uppercase tracking-widest text-white">
                  Grátis
                </ThemedText>
              </View>
            </View>
          </AnimatedView>
        )}

        <View className={`relative overflow-hidden px-4 pb-6 ${isNative ? 'pt-4' : 'pb-10 pt-16'}`}>
          <View className="absolute -left-24 -top-24 h-64 w-64 rounded-full bg-primary-50" />
          <View className="absolute -bottom-32 -right-32 h-80 w-80 rounded-full bg-primary-50" />

          <View className="relative z-10 mx-auto w-full max-w-2xl items-center">
            <AnimatedView entering={FadeInDown.duration(500).delay(100)}>
              <ThemedText className="mb-3 font-bold text-sm uppercase tracking-widest text-primary">
                Assinatura mensal
              </ThemedText>
            </AnimatedView>
            <AnimatedView entering={FadeInDown.duration(600).delay(150)}>
              <ThemedText
                className={`mb-3 text-center font-extrabold tracking-tight text-neutral-900 ${
                  isNative ? 'text-3xl' : 'text-4xl'
                }`}
              >
                {isTeamBilling ? 'Monte o plano da equipe' : 'Escolha o plano ideal'}
              </ThemedText>
            </AnimatedView>
            <AnimatedView entering={FadeInDown.duration(600).delay(200)}>
              <ThemedText className="text-center text-base leading-relaxed text-neutral-500">
                {isFreePlan
                  ? 'Você está no plano gratuito. Assine para ter WhatsApp automático, recebimento automático, perfil público, assistente e assinaturas.'
                  : 'Pagamento seguro. Cancele quando quiser.'}
              </ThemedText>
            </AnimatedView>
          </View>
        </View>

        <View className="pb-4">
          {isTeamBilling ? (
            <View className="mx-auto w-full max-w-xl px-4">
              <TeamPlansPanel appleIAP={appleIAP} />
            </View>
          ) : useCarousel ? (
            <>
              <View className="mb-3 flex-row items-center justify-center gap-1.5 px-4">
                <Ionicons className="text-neutral-400" name="swap-horizontal-outline" size={14} />
                <ThemedText className="text-center text-xs text-neutral-400">
                  Deslize para comparar os planos
                </ThemedText>
              </View>
              <ScrollView
                keyboardShouldPersistTaps="handled"
                horizontal
                showsHorizontalScrollIndicator={false}
                decelerationRate="fast"
                snapToInterval={carouselSnapInterval}
                snapToAlignment="start"
                disableIntervalMomentum
                nestedScrollEnabled
                directionalLockEnabled
                style={{ flexGrow: 0 }}
                contentContainerStyle={{
                  paddingHorizontal: CAROUSEL_PADDING,
                  paddingVertical: 4,
                  // stretch: todos os cards ficam com a altura do mais alto
                  alignItems: 'stretch',
                }}
              >
                {plans.map((plan, index) => (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    index={index}
                    cardWidth={carouselCardWidth}
                    marginRight={index < plans.length - 1 ? CAROUSEL_GAP : 0}
                    introPrice={introPriceOf(plan.id)}
                    {...planCardProps}
                  />
                ))}
              </ScrollView>
            </>
          ) : (
            <View
              className="mx-auto w-full px-4"
              style={
                {
                  maxWidth: 1180,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                  alignItems: 'stretch',
                  gap: 24,
                } as any
              }
            >
              {plans.map((plan, index) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  index={index}
                  introPrice={introPriceOf(plan.id)}
                  {...planCardProps}
                />
              ))}
            </View>
          )}
        </View>

        {membership && isSolo && isOwner && teamPlanAvailable ? (
          <View className="mt-2 items-center px-4">
            <Pressable onPress={() => setBecomeTeamOpen(true)} className="py-2">
              <ThemedText className="text-center text-sm text-neutral-500">
                Trabalha com mais gente?{' '}
                <ThemedText className="font-semibold text-sm text-primary">
                  Montar equipe
                </ThemedText>
              </ThemedText>
            </Pressable>
            <BecomeTeamSheet open={becomeTeamOpen} onClose={() => setBecomeTeamOpen(false)} />
          </View>
        ) : null}

        <AnimatedView
          entering={FadeInDown.duration(400).delay(600)}
          className="mt-4 w-full items-center px-4"
        >
          <View className="max-w-full flex-row items-center gap-2 rounded-full bg-neutral-100 px-4 py-2">
            <Ionicons className="text-neutral-500" name="shield-checkmark-outline" size={16} />
            <ThemedText className="shrink text-xs text-neutral-500">
              {Platform.OS === 'ios'
                ? 'Pagamento pela sua conta Apple'
                : Platform.OS === 'android'
                  ? 'Pagamento pela sua conta Google'
                  : 'Pagamento seguro com cartão de crédito'}
            </ThemedText>
          </View>
        </AnimatedView>

        {isFreePlan && !isTeamBilling && (
          <AnimatedView
            entering={FadeInDown.duration(400).delay(640)}
            className="mt-2 w-full items-center px-4"
          >
            <Pressable
              onPress={() => {
                if (router.canGoBack()) router.back();
                else router.replace('/(tabs)/(provider)/home');
              }}
              className="py-2"
            >
              <ThemedText className="text-center text-sm text-neutral-500">
                Continuar no{' '}
                <ThemedText className="font-semibold text-sm text-primary">
                  plano gratuito
                </ThemedText>
              </ThemedText>
            </Pressable>
          </AnimatedView>
        )}

        {isStoreBilling && (
          <AnimatedView
            entering={FadeInDown.duration(400).delay(650)}
            className="mt-3 w-full items-center px-4"
          >
            <Pressable
              disabled={isApplePurchasing}
              onPress={async () => {
                const restored = await appleIAP.restorePurchases();
                showToast(
                  restored
                    ? 'Compras restauradas com sucesso.'
                    : 'Nenhuma compra anterior encontrada.',
                  restored ? 'success' : 'info',
                );
              }}
            >
              <ThemedText className="font-semibold text-sm text-primary underline">
                Restaurar compras
              </ThemedText>
            </Pressable>
          </AnimatedView>
        )}

        {isStoreBilling && (
          <View className="mx-auto mt-4 w-full max-w-md gap-2 px-6">
            <ThemedText className="text-center text-xs leading-relaxed text-neutral-500">
              {Platform.OS === 'ios'
                ? 'Assinatura mensal com renovação automática. A cobrança é feita na sua conta Apple ao confirmar a compra e se repete todo mês, a menos que você cancele até 24 horas antes do fim do período. Gerencie ou cancele em Ajustes › seu nome › Assinaturas.'
                : 'Assinatura mensal com renovação automática. A cobrança é feita na sua conta Google ao confirmar a compra e se repete todo mês até você cancelar. Gerencie ou cancele no Google Play › Pagamentos e assinaturas.'}
            </ThemedText>
            <View className="flex-row justify-center gap-4">
              <Pressable onPress={() => router.push('/terms')} hitSlop={8}>
                <ThemedText className="text-xs text-primary underline">Termos de Uso</ThemedText>
              </Pressable>
              <Pressable onPress={() => router.push('/privacidade')} hitSlop={8}>
                <ThemedText className="text-xs text-primary underline">
                  Política de Privacidade
                </ThemedText>
              </Pressable>
            </View>
          </View>
        )}
      </ScrollView>
    </View>
  );
}
